'use client';

// module imports
import { zodResolver } from '@hookform/resolvers/zod';
import { Shield } from 'lucide-react';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

// file imports
import { FormField } from '@/components';

const schema = z.object({
  email: z.string().min(1, 'Email is required').email({ message: 'Please enter a valid email' }),
  password: z.string().min(1, 'Password is required').min(6, { message: 'Password must be at least 6 characters' }),
});

type AdminLoginFormData = z.infer<typeof schema>;

interface AdminLoginFormProps {
  onSuccess?: () => void;
}

export default function AdminLoginForm({ onSuccess }: AdminLoginFormProps) {
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AdminLoginFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: AdminLoginFormData) => {
    setLoading(true);
    setErrorMsg('');

    // Lowercase the email before sending
    const lowercasedEmail = data.email.toLowerCase();

    const res: any = await signIn('credentials', {
      email: lowercasedEmail,
      password: data.password,
      userType: 'admin',
      redirect: false,
    });

    setLoading(false);

    if (res?.error) {
      setErrorMsg('Invalid admin credentials');
      return;
    }

    if (onSuccess) {
      onSuccess();
    } else {
      router.push('/admin/dashboard');
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-blue-100 p-3 rounded-full">
              <Shield className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Login</h1>
          <p className="text-gray-600 mt-2">Access the admin dashboard</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <FormField id="email" label="Email Address" type="email" placeholder="admin@example.com" {...register('email')} error={errors.email?.message} required />
          <div className="mt-2"></div>
          <FormField id="password" label="Password" type="password" placeholder="Enter your password" {...register('password')} error={errors.password?.message} required />
          {errorMsg && <div className="text-red-600 mb-3 text-sm">{errorMsg}</div>}
          <div className="mt-3"></div>
          <button type="submit" className="w-full py-3 bg-[#5167b2] text-white font-bold rounded-lg hover:bg-[#4158a2] cursor-pointer transition" disabled={loading}>
            {loading ? 'Logging in...' : 'LOGIN'}
          </button>
        </form>
      </div>
    </div>
  );
}
